import {useMemo} from 'react';

import {FieldCombobox, resolveOptionLabel} from '../FieldCombobox';

import type {FieldComboboxOption, FieldComboboxProps} from '../FieldCombobox';

export interface BankProviderInfo {
  id: string;
  name: string;
  nameNativeLanguage: string;
  logoUrl: string;
  bankNumber: number | null;
}

export interface BankProviderFieldProps extends Omit<
  FieldComboboxProps,
  'options' | 'groups' | 'value' | 'onValueChange' | 'renderValue'
> {
  providers: BankProviderInfo[];
  value: string;
  onValueChange: (providerId: string) => void;
  /** e.g. "Bank" — rendered before the bank number as `"{bankNumberLabel} {bankNumber}"`. */
  bankNumberLabel: string;
}

/** Bank picker keyed by provider id, not bank number — never collides even when two providers share a bank number. */
export function BankProviderField({
  providers,
  bankNumberLabel,
  ...fieldProps
}: BankProviderFieldProps) {
  const options = useMemo(
    () => buildBankProviderOptions(providers, bankNumberLabel),
    [providers, bankNumberLabel]
  );
  return (
    <FieldCombobox
      {...fieldProps}
      options={options}
      renderValue={id => resolveOptionLabel(options, id)}
    />
  );
}

export function buildBankProviderOptions(
  providers: BankProviderInfo[],
  bankNumberLabel: string
): FieldComboboxOption[] {
  return providers.map(provider => ({
    value: provider.id,
    searchText:
      `${provider.bankNumber ?? ''} ${provider.nameNativeLanguage} ${provider.name}`.trim(),
    label: <BankProviderOptionLabel provider={provider} bankNumberLabel={bankNumberLabel} />
  }));
}

interface BankProviderOptionLabelProps {
  provider: BankProviderInfo;
  bankNumberLabel: string;
}

function BankProviderOptionLabel({provider, bankNumberLabel}: BankProviderOptionLabelProps) {
  return (
    <span className="flex w-full items-center gap-2">
      <img src={provider.logoUrl} alt="" className="size-7.5 shrink-0 rounded-lg object-contain" />
      <span className="font-semibold text-sm whitespace-nowrap">{provider.nameNativeLanguage}</span>
      {provider.bankNumber != null && (
        <span className="text-caption text-fg-tertiary whitespace-nowrap">
          {`${bankNumberLabel} ${provider.bankNumber}`}
        </span>
      )}
    </span>
  );
}
